import { BlockType, ContentType } from '../../../models/enums';
import { DeletionType } from '../../../common/enums';
import { getAdjacentBlock, getBlockContentElement, getBlockModelById, isNonContentEditableBlock } from '../../../common/utils/block';
import * as constants from '../../../common/constant';
import { actionType } from '../../../common/constant';
import { setCursorPosition } from '../../../common/utils/selection';
import { decoupleReference, findCellById, getDataCell, getTableElements, toDomCol, toDomRow } from '../../../common/index';
import { isNullOrUndefined } from '@syncfusion/ej2-base';
/**
 * Manages undo redo actions for the BlockEditor component
 */
export class UndoRedoManager {
    constructor(manager, action) {
        this.parent = manager;
        this.undoRedoAction = action;
    }
    /**
     * Renders the block with the previous state
     *
     * @param {string} blockId - Specifies the block id
     * @param {BlockModel} oldBlock - Specifies the old block model
     * @param {BlockModel} newBlock - Specifies the new block model
     * @returns {void} - Returns void
     * @hidden
     */
    reRenderWithPreviousState(blockId, oldBlock, newBlock) {
        const targetBlockModel = getBlockModelById(blockId, this.parent.getEditorBlocks());
        if (!targetBlockModel) {
            return;
        }
        const blockToReplace = this.undoRedoAction.isUndoing ? oldBlock : newBlock;
        this.parent.blockService.replaceBlock(targetBlockModel.id, blockToReplace);
        this.parent.stateManager.updateManagerBlocks();
        this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                data: [{ block: blockToReplace, oldBlock: oldBlock }]
            } });
    }
    /**
     * Handles the undo redo for formatting action
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    handleFormattingUndoRedo(currentState) {
        const data = currentState.data;
        // On Redo, restore the contentchange action first and proceed with formatting action(applicable only for format on user typing)
        if (this.undoRedoAction.isRedoing && data.isTypingWithFormat) {
            this.undoRedoAction.applyNextRedoSibling();
        }
        data.blockIDs.forEach((blockId) => {
            this.reRenderWithPreviousState(blockId, data.oldBlockModels.find((block) => block.id === blockId), data.updatedBlockModels.find((block) => block.id === blockId));
        });
        // On Undo, restore the contentchange action after processing formatting action(applicable only for format on user typing)
        if (this.undoRedoAction.isUndoing && data.isTypingWithFormat) {
            this.undoRedoAction.applyNextUndoSibling();
        }
        if (!data.isTypingWithFormat) {
            this.parent.formattingAction.nodeSelection.savedSelectionState = data.selectionState;
            this.parent.formattingAction.nodeSelection.restoreSelection();
            this.parent.setFocusToBlock(this.parent.getBlockElementById(data.selectionState.startBlockId));
        }
    }
    /**
     * Moves the blocks into its original position
     *
     * @param {IMoveBlocksInteraction} args - Specifies the arguments for moving the blocks
     * @returns {void} - Returns void
     * @hidden
     */
    moveBlocksIntoOriginalPosition(args) {
        const { fromBlockIds, fromIndex, fromParentId, toParentId } = args;
        const editorBlocks = this.parent.getEditorBlocks();
        // Collect current indexes before mutating the array
        const fromEntries = this.parent.blockService.gatherBlocksInfoForMove(fromBlockIds);
        let oldDatas = this.parent.blockService.removeBlocksForMove(fromEntries);
        // Collect the old data models
        oldDatas = [...fromEntries].reverse()
            .map((fromEntry, i) => {
            const index = fromIndex[parseInt(i.toString(), 10)];
            const parent = getBlockModelById(fromParentId[parseInt(i.toString(), 10)], editorBlocks);
            return Object.assign({}, fromEntry, { index, parent });
        });
        let targetId = '';
        // insert in its old position
        for (const entry of oldDatas) {
            const { model: entryModel, index, parent } = entry;
            const insertToArray = parent ? parent.properties.children : editorBlocks;
            if (targetId === '') {
                // to get the correct target id for the moved blocks.
                const targetBlockModel = insertToArray[args.isMovedUp ? index - 1 : index];
                targetId = targetBlockModel.id;
            }
            entryModel.parentId = parent ? parent.id : '';
            insertToArray.splice(index, 0, entryModel);
        }
        this.parent.stateManager.updateManagerBlocks();
        oldDatas.forEach((data) => {
            const prevParent = fromEntries.find((fromModel) => fromModel.parent !== null);
            const currParent = getBlockModelById(data.model.parentId, editorBlocks);
            this.parent.eventService.addChange({
                action: 'Moved',
                data: {
                    block: data.model,
                    targetId: targetId,
                    isMovingUp: !args.isMovedUp,
                    prevParent: prevParent ? prevParent.model : undefined,
                    currentParent: currParent ? currParent : undefined
                }
            });
        });
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
        // DOM updates
        for (const entry of args.isMovedUp ? oldDatas.reverse() : oldDatas) {
            const { blockId, index, parent } = entry;
            const fromElement = this.parent.blockContainer.querySelector(`#${blockId}`);
            const allBlocks = Array.from(this.parent.blockContainer.children);
            // should reduce index only when any block is moved into a special block or last child block is moved outside from a special block (Callout, Toggle)
            const shouldReduceIndex = parent
                ? parent.properties.children[(parent.properties
                    .children.length - 1)].id === blockId
                : toParentId !== '';
            const indexVal = shouldReduceIndex ? index - 1 : index;
            const parentElement = (parent ? this.parent.blockContainer.querySelector(`#${parent.id}`) : null);
            const toBlockDOM = (parent
                ? parentElement.querySelectorAll('.' + constants.BLOCK_CLS)[parseInt(indexVal.toString(), 10)]
                : allBlocks[parseInt(indexVal.toString(), 10)]);
            let wrapperClassName = '';
            if (parent) {
                switch (parent.blockType) {
                    case BlockType.Callout:
                        wrapperClassName = '.' + constants.CALLOUT_CONTENT_CLS;
                        break;
                    case BlockType.Quote:
                        wrapperClassName = '.' + constants.QUOTE_CONTENT_CLS;
                        break;
                    default:
                        if (parent.blockType.toString().startsWith('Collapsible')) {
                            wrapperClassName = '.' + constants.TOGGLE_CONTENT_CLS;
                        }
                        break;
                }
            }
            const wrapperElement = wrapperClassName
                ? parentElement.querySelector(wrapperClassName)
                : this.parent.blockContainer;
            let targetToInsert;
            if (!args.isMovedUp) {
                targetToInsert = (shouldReduceIndex
                    ? (toBlockDOM ? toBlockDOM.nextElementSibling : toBlockDOM)
                    : toBlockDOM);
            }
            else {
                targetToInsert = (toBlockDOM ? toBlockDOM.nextElementSibling : toBlockDOM);
            }
            wrapperElement.insertBefore(fromElement, targetToInsert);
        }
        const blockIdToFocus = fromBlockIds[0];
        const blockElementToFocus = this.parent.getBlockElementById(blockIdToFocus);
        this.parent.updateFocusAndCursor(blockElementToFocus);
    }
    /**
     * Handles the block movement undo redo action
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    handleBlockMovement(currentState) {
        const moveData = currentState.data;
        const moveBlockArgs = {
            fromBlockIds: moveData.blockIds,
            fromIndex: moveData.fromIndex,
            toBlockId: moveData.toBlockId,
            toIndex: moveData.toIndex,
            fromParentId: moveData.fromParentId,
            toParentId: moveData.toParentId,
            isUndoRedoAction: true,
            isMovedUp: moveData.isMovedUp
        };
        if (this.undoRedoAction.isUndoing) {
            this.moveBlocksIntoOriginalPosition(moveBlockArgs);
        }
        else {
            this.parent.execCommand({ command: 'MoveBlock', state: moveBlockArgs });
        }
    }
    /**
     * Re-transforms the block with the previous state
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    reTransformBlocks(currentState) {
        const transformedData = currentState.data;
        if (!transformedData) {
            return;
        }
        const currentBlockModel = transformedData.blockId
            ? getBlockModelById(transformedData.blockId, this.parent.getEditorBlocks()) : null;
        const storedBlockModel = this.undoRedoAction.isUndoing
            ? transformedData.oldBlockModel : transformedData.newBlockModel;
        const blockElement = this.parent.blockContainer.querySelector(`#${transformedData.blockId}`);
        const newBlockType = this.undoRedoAction.isUndoing
            ? transformedData.oldBlockModel.blockType : transformedData.newBlockModel.blockType;
        this.parent.blockCommand.handleBlockTransformation({
            block: currentBlockModel,
            blockElement: blockElement,
            newBlockType: newBlockType,
            isUndoRedoAction: true,
            props: storedBlockModel.properties,
            oldBlockModel: storedBlockModel
        });
    }
    /**
     * Handles undo/redo action for image insertion (placeholder → uploaded image)
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    handleImageInsertion(currentState) {
        const imageData = currentState.data;
        if (!imageData) {
            return;
        }
        const blockId = imageData.blockId;
        const blockElement = this.parent.blockContainer.querySelector(`#${blockId}`);
        if (!blockElement) {
            return;
        }
        // replace the block model in the editor
        this.parent.blockService.replaceBlock(blockId, this.undoRedoAction.isUndoing ? imageData.oldBlockModel : imageData.newBlockModel);
        this.parent.stateManager.updateManagerBlocks();
        // Re-render the block content to reflect the model change
        this.parent.observer.notify('modelChanged', { type: 'ReplaceBlock', state: {
                targetBlockId: blockId,
                block: this.undoRedoAction.isUndoing ? imageData.oldBlockModel : imageData.newBlockModel,
                oldBlock: this.undoRedoAction.isUndoing ? imageData.newBlockModel : imageData.oldBlockModel
            } });
        // Set focus to the block
        this.parent.setFocusToBlock(blockElement);
    }
    /**
     * Handles undo redo action for multiple block transformations
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    reTransformMultipleBlocks(currentState) {
        const multiTransformData = currentState.data;
        if (!multiTransformData || !multiTransformData.transformedBlocks || multiTransformData.transformedBlocks.length === 0) {
            return;
        }
        // Prevent individual undo tracking during batch redo
        this.undoRedoAction.beginBatchTransform();
        for (const transform of multiTransformData.transformedBlocks) {
            const currentBlockModel = getBlockModelById(transform.blockId, this.parent.getEditorBlocks());
            const storedBlockModel = this.undoRedoAction.isUndoing
                ? transform.oldBlockModel : transform.newBlockModel;
            const blockElement = this.parent.blockContainer.querySelector(`#${transform.blockId}`);
            const newBlockType = this.undoRedoAction.isUndoing
                ? transform.oldBlockModel.blockType : transform.newBlockModel.blockType;
            if (currentBlockModel && blockElement) {
                this.parent.blockCommand.handleBlockTransformation({
                    block: currentBlockModel,
                    blockElement: blockElement,
                    newBlockType: newBlockType,
                    isUndoRedoAction: true,
                    props: storedBlockModel.properties,
                    oldBlockModel: storedBlockModel
                });
            }
        }
        // End batch mode without pushing to undo stack
        this.undoRedoAction.endBatchTransform();
    }
    /**
     * Handles undo redo action for multiple block deletions
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    handleMultipleBlocksUndoRedo(currentState) {
        if (this.undoRedoAction.isUndoing) {
            this.restoreDeletedBlocks(currentState);
        }
        else if (this.undoRedoAction.isRedoing) {
            this.reDeleteBlocks(currentState);
        }
    }
    restoreDeletedBlocks(state) {
        const data = state.data;
        if (data.deletionType === DeletionType.Entire) {
            this.restoreEntireEditor(data.deletedBlocks);
            setTimeout(() => {
                this.parent.editorMethods.selectAllBlocks();
            });
        }
        else if (data.deletionType === DeletionType.Partial) {
            this.restorePartialDeletion(state);
        }
        this.parent.stateManager.updateManagerBlocks();
    }
    restoreEntireEditor(deletedBlocks) {
        this.parent.setEditorBlocks(deletedBlocks);
        this.parent.blockContainer.innerHTML = '';
        this.parent.blockRenderer.renderBlocks(deletedBlocks);
    }
    restorePartialDeletion(state) {
        const { deletedBlocks, firstBlockIndex } = state.data;
        if (!deletedBlocks.length) {
            return;
        }
        const [firstBlock, ...middleBlocks] = deletedBlocks;
        const lastBlock = middleBlocks.pop();
        const firstBlockParent = getBlockModelById(firstBlock.parentId, this.parent.getEditorBlocks())
            || findCellById(firstBlock.parentId, this.parent.getEditorBlocks());
        const targetIndex = (firstBlockIndex - 1) >= 0 ? (firstBlockIndex - 1) : 0;
        let targetBlockElement = this.parent.blockContainer.children[targetIndex];
        if (firstBlockParent) {
            const childBlocks = this.parent.getBlockElementById(firstBlockParent.id).querySelectorAll('.' + constants.BLOCK_CLS);
            targetBlockElement = childBlocks[targetIndex];
        }
        const firstBlockElement = this.restoreSingleBlock(firstBlock, firstBlockIndex, targetBlockElement, true);
        this.restoreSingleBlock(lastBlock, firstBlockIndex + 1, firstBlockElement);
        if (middleBlocks.length) {
            this.restoreMiddleBlocks(middleBlocks, firstBlockElement);
        }
        this.parent.listPlugin.recalculateMarkersForListItems();
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    restoreSingleBlock(block, insertIndex, targetElement, isFirstBlock) {
        const editorBlocks = this.parent.getEditorBlocks();
        let blockElement = this.parent.getBlockElementById(block.id);
        const parent = getBlockModelById(block.parentId, editorBlocks);
        const tableParent = findCellById(block.parentId, editorBlocks);
        const deleteCount = isFirstBlock ? 1 : 0;
        const insertArray = parent
            ? parent.properties.children
            : (tableParent ? tableParent.blocks : editorBlocks);
        insertArray.splice(insertIndex, deleteCount, decoupleReference(block));
        if (blockElement) {
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: block }],
                    preventEventTrigger: true
                } });
            return blockElement;
        }
        this.parent.eventService.addChange({
            action: 'Insertion',
            data: { block: block }
        });
        blockElement = this.parent.blockRenderer.createBlockElement(block);
        this.parent.togglePlaceholder(blockElement, false);
        if (targetElement) {
            targetElement.insertAdjacentElement(insertIndex === 0 ? 'beforebegin' : 'afterend', blockElement);
        }
        return blockElement;
    }
    restoreMiddleBlocks(middleBlocks, targetElement) {
        let currentInsertionPoint = targetElement;
        for (const block of middleBlocks) {
            this.parent.execCommand({ command: 'AddBlock', state: {
                    block,
                    targetBlock: currentInsertionPoint,
                    isUndoRedoAction: true,
                    preventEventTrigger: true
                } });
            currentInsertionPoint = this.parent.getBlockElementById(block.id);
            this.parent.togglePlaceholder(currentInsertionPoint, false);
        }
    }
    reDeleteBlocks(state) {
        const data = state.data;
        if (data.deletionType === DeletionType.Entire) {
            this.parent.setEditorBlocks([]);
            this.parent.blockCommand.createDefaultEmptyBlock(true, state.oldBlockModel ? state.oldBlockModel.id : '');
        }
        else if (data.deletionType === DeletionType.Partial) {
            const blocksToDelete = [];
            for (let i = 0; i < data.deletedBlocks.length; i++) {
                const block = data.deletedBlocks[i];
                const currentBlockModel = getBlockModelById(block.id, this.parent.getEditorBlocks());
                if (currentBlockModel) {
                    blocksToDelete.push(currentBlockModel);
                }
            }
            if (blocksToDelete.length > 0) {
                this.undoRedoAction.restoreSelectionState(state);
                this.parent.blockCommand.handleMultipleBlockDeletion(blocksToDelete, data.direction || 'previous', true);
            }
        }
    }
    /**
     * Handles the clipboard undo redo action
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    handleClipboardActions(currentState) {
        if (this.undoRedoAction.isUndoing) {
            this.handleClipboardUndo(currentState);
        }
        else {
            this.handleClipboardRedo(currentState);
        }
    }
    /**
     * Applies a table row action for undo/redo.
     * Decides whether to insert or delete a row based on action intent and current undo/redo phase.
     * When apply is 'insert':
     *  - Undo => perform delete
     *  - Redo => perform insert
     * When apply is 'delete':
     *  - Undo => perform insert
     *  - Redo => perform delete
     *
     * @param { ITableRowInsertOptions } data Payload that includes blockId, rowIndex and optional rowModel
     * @param { string } apply Indicates target action recorded in the stack ('insert' | 'delete')
     * @returns {void} - Returns void
     * @hidden
     */
    processRowAction(data, apply) {
        const shouldInsert = (apply === 'insert' && !this.undoRedoAction.isUndoing)
            || (apply === 'delete' && this.undoRedoAction.isUndoing);
        if (shouldInsert) {
            this.parent.tableService.addRowAt({
                blockId: data.blockId,
                rowIndex: data.rowIndex,
                rowModel: data.rowModel,
                isUndoRedoAction: true
            });
        }
        else {
            this.parent.tableService.deleteRowAt({
                blockId: data.blockId,
                modelIndex: data.rowIndex,
                isUndoRedoAction: true
            });
        }
    }
    /**
     * Applies a table column action for undo/redo.
     * Decides whether to insert or delete a column based on action intent and current undo/redo phase.
     * When apply is 'insert':
     *  - Undo => perform delete
     *  - Redo => perform insert
     * When apply is 'delete':
     *  - Undo => perform insert
     *  - Redo => perform delete
     *
     * @param { ITableColumnInsertOptions } data Payload that includes blockId, colIndex and optional columnModel
     * @param { string } apply Indicates target action recorded in the stack ('insert' | 'delete')
     * @returns {void} - Returns void
     * @hidden
     */
    processColumnAction(data, apply) {
        const shouldInsert = (apply === 'insert' && !this.undoRedoAction.isUndoing)
            || (apply === 'delete' && this.undoRedoAction.isUndoing);
        if (shouldInsert) {
            this.parent.tableService.addColumnAt({
                blockId: data.blockId,
                colIndex: data.colIndex,
                columnModel: data.columnModel,
                columnCells: data.columnCells,
                isUndoRedoAction: true
            });
        }
        else {
            this.parent.tableService.deleteColumnAt({
                blockId: data.blockId,
                colIndex: data.colIndex,
                isUndoRedoAction: true
            });
        }
    }
    /**
     * Handles undo/redo for table cell clearing.
     * On undo: restores each affected cell's previous blocks.
     * On redo: clears the blocks for each affected cell.
     *
     * @param { IUndoRedoState } state that contains blockId and a list of cells with previous blocks
     * @returns {void} - Returns void
     * @hidden
     */
    handleTableCellsCleared(state) {
        const data = state.data;
        const ctx = getTableElements(data.blockId, this.parent.rootEditorElement, this.parent.getEditorBlocks());
        if (!ctx) {
            return;
        }
        const { table } = ctx;
        const oldBlock = decoupleReference(getBlockModelById(data.blockId, this.parent.getEditorBlocks()));
        const mode = this.undoRedoAction.isUndoing ? 'restore' : 'clear';
        data.cells.forEach((cell) => this.parent.tableService.applyCellChange(table, cell, mode));
        const updatedBlock = decoupleReference(getBlockModelById(data.blockId, this.parent.getEditorBlocks()));
        this.parent.tableService.triggerBlockUpdate(updatedBlock, oldBlock);
        const blockId = data.blockId;
        const blockEle = this.parent.getBlockElementById(blockId);
        if (blockEle) {
            this.parent.selectionOverlay.show(blockId);
            if (this.parent.nodeSelection) {
                this.parent.nodeSelection.clearSelection();
            }
        }
    }
    handleTableCellsPasted(state) {
        const data = state.data;
        const ctx = getTableElements(data.blockId, this.parent.rootEditorElement, this.parent.getEditorBlocks());
        if (!ctx) {
            return;
        }
        const { table, props } = ctx;
        const oldBlock = decoupleReference(getBlockModelById(data.blockId, this.parent.getEditorBlocks()));
        const applyBlocks = (useNew) => {
            data.cells.forEach((cell) => {
                const blocks = useNew ? cell.newBlocks : cell.oldBlocks;
                this.parent.tableService.setCellBlocks(table, cell.dataRow, cell.dataCol, blocks);
            });
        };
        if (this.undoRedoAction.isUndoing) {
            // Remove structure added on paste
            if (data.structureDelta) {
                if (data.structureDelta.colsAdded && data.structureDelta.colsAdded.length) {
                    [...data.structureDelta.colsAdded].sort((a, b) => b - a).forEach((c) => {
                        this.parent.tableService.deleteColumnAt({ blockId: data.blockId, colIndex: c, isUndoRedoAction: true });
                    });
                }
                if (data.structureDelta.rowsAdded && data.structureDelta.rowsAdded.length) {
                    [...data.structureDelta.rowsAdded].sort((a, b) => b - a).forEach((r) => {
                        this.parent.tableService.deleteRowAt({ blockId: data.blockId, modelIndex: r, isUndoRedoAction: true });
                    });
                }
            }
            applyBlocks(false);
        }
        else {
            // Rebuild structure before reapplying new blocks
            if (data.structureDelta) {
                if (data.structureDelta.rowsAdded && data.structureDelta.rowsAdded.length) {
                    [...data.structureDelta.rowsAdded].sort((a, b) => a - b).forEach((r) => {
                        this.parent.tableService.addRowAt({ blockId: data.blockId, rowIndex: r, isUndoRedoAction: true });
                    });
                }
                if (data.structureDelta.colsAdded && data.structureDelta.colsAdded.length) {
                    [...data.structureDelta.colsAdded].sort((a, b) => a - b).forEach((c) => {
                        this.parent.tableService.addColumnAt({ blockId: data.blockId, colIndex: c, isUndoRedoAction: true });
                    });
                }
            }
            applyBlocks(true);
        }
        // Restore cell focus to first cell
        if (data.cells[0]) {
            const cellToFocus = getDataCell(table, toDomRow(data.cells[0].dataRow, props.enableHeader), data.cells[0].dataCol);
            this.parent.tableService.removeCellFocus(table);
            this.parent.tableService.addCellFocus(cellToFocus, true);
        }
        const updatedBlock = decoupleReference(getBlockModelById(data.blockId, this.parent.getEditorBlocks()));
        this.parent.tableService.triggerBlockUpdate(updatedBlock, oldBlock);
    }
    handleBulkRowsDeleted(currentState) {
        const data = currentState.data;
        const ctx = getTableElements(data.blockId, this.parent.rootEditorElement, this.parent.getEditorBlocks());
        if (!ctx) {
            return;
        }
        if (this.undoRedoAction.isUndoing) {
            // Re-insert rows at their original indices (ascending)
            [...data.rows].sort((a, b) => a.index - b.index).forEach((r) => {
                this.parent.tableService.addRowAt({
                    blockId: data.blockId,
                    rowIndex: r.index,
                    rowModel: r.rowModel,
                    isUndoRedoAction: true
                });
            });
        }
        else {
            // Re-delete rows (descending DOM order)
            [...data.rows].sort((a, b) => b.index - a.index).forEach((r) => {
                this.parent.tableService.deleteRowAt({
                    blockId: data.blockId,
                    modelIndex: r.index,
                    isUndoRedoAction: true
                });
            });
        }
    }
    handleBulkColumnsDeleted(currentState) {
        const data = currentState.data;
        const ctx = getTableElements(data.blockId, this.parent.rootEditorElement, this.parent.getEditorBlocks());
        if (!ctx) {
            return;
        }
        if (this.undoRedoAction.isUndoing) {
            // Re-insert columns (ascending index)
            [...data.cols].sort((a, b) => a.index - b.index).forEach((c) => {
                this.parent.tableService.addColumnAt({
                    blockId: data.blockId,
                    colIndex: c.index,
                    columnModel: c.columnModel,
                    columnCells: c.columnCells,
                    isUndoRedoAction: true
                });
            });
        }
        else {
            // Re-delete columns (descending index)
            [...data.cols].sort((a, b) => b.index - a.index).forEach((c) => {
                this.parent.tableService.deleteColumnAt({
                    blockId: data.blockId,
                    colIndex: c.index,
                    isUndoRedoAction: true
                });
            });
        }
    }
    handleTableHeaderUndoRedo(state) {
        const data = state.data;
        const blockModel = getBlockModelById(data.blockId, this.parent.getEditorBlocks());
        if (!blockModel) {
            return;
        }
        const blockElement = this.parent.getBlockElementById(blockModel.id);
        const props = blockModel.properties;
        const tableHeaders = blockElement.querySelectorAll('thead th:not(.e-row-number)');
        const columnsToReplace = this.undoRedoAction.isUndoing ? data.oldColumns : data.updatedColumns;
        tableHeaders.forEach((headerElement) => {
            const colIndex = parseInt(headerElement.getAttribute('data-col'), 10);
            const textToUpdate = columnsToReplace[colIndex].headerText;
            //DOM and Model
            headerElement.textContent = props.columns[colIndex].headerText = textToUpdate;
        });
    }
    handleTableColumnResized(state) {
        const data = state.data;
        const blockModel = getBlockModelById(data.blockId, this.parent.getEditorBlocks());
        if (!blockModel) {
            return;
        }
        const props = blockModel.properties;
        const blockEl = this.parent.getBlockElementById(blockModel.id);
        const table = blockEl.querySelector('table.e-table-element');
        const colgroup = table.querySelector('colgroup');
        const useOld = this.undoRedoAction.isUndoing;
        const updatedWidth = useOld ? data.oldWidthValue : data.newWidthValue;
        // Update model
        props.columns[data.resizedColIndex].width = `${updatedWidth}px`;
        // Update DOM colgroup
        const domLeftIdx = toDomCol(data.resizedColIndex, props.enableRowNumbers);
        colgroup.children[domLeftIdx].style.width = `${updatedWidth}px`;
    }
    handleClipboardUndo(currentState) {
        const { type, blocks, targetBlockId, clipboardData, oldContent, isPastedAtStart, isSelectivePaste, cursorBlockAfterSplit } = currentState.data;
        const targetBlock = getBlockModelById(targetBlockId, this.parent.getEditorBlocks());
        const oldTargetBlock = decoupleReference(targetBlock);
        if (type === 'blocks') {
            const clipboardBlocks = clipboardData.blocks;
            const nonMergableTypes = [BlockType.Table, BlockType.Image, BlockType.Divider];
            const isFirstBlkNonMergableType = clipboardBlocks ?
                nonMergableTypes.indexOf(clipboardData.blocks[0].blockType) !== -1 : false;
            const oldBlock = decoupleReference(currentState.oldBlockModel);
            const isEmptyTargetBlock = oldBlock && oldBlock.content
                && ((oldBlock.content.length === 1 && oldBlock.content[0].contentType === ContentType.Text &&
                    (oldBlock.content[0].content === '' || !oldBlock.content[0].content))
                    || !oldBlock.content.length);
            if (blocks && blocks.length > 0) {
                blocks.forEach((block) => {
                    this.parent.blockCommand.deleteBlock({
                        blockElement: this.parent.getBlockElementById(block.id),
                        isUndoRedoAction: true,
                        preventEventTrigger: true
                    });
                });
            }
            if (isEmptyTargetBlock) {
                this.parent.blockService.replaceBlock(targetBlock.id, oldBlock);
                this.parent.observer.notify('modelChanged', { type: 'ReplaceBlock', state: {
                        targetBlockId: targetBlockId,
                        block: oldBlock,
                        oldBlock: oldTargetBlock,
                        preventEventTrigger: true
                    } });
            }
            else if (!isFirstBlkNonMergableType) {
                this.parent.blockService.updateContent(targetBlock.id, cursorBlockAfterSplit.content);
                this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                        data: [{ block: targetBlock, oldBlock: oldTargetBlock }],
                        preventEventTrigger: true
                    } });
                if (!isSelectivePaste) {
                    this.undoRedoAction.applyNextUndoSibling();
                }
            }
        }
        else if (type === 'block') {
            this.parent.blockCommand.deleteBlock({
                blockElement: this.parent.getBlockElementById(blocks[0].id),
                isUndoRedoAction: true,
                preventEventTrigger: true
            });
        }
        else if (type === 'content') {
            this.parent.blockService.updateContent(targetBlock.id, oldContent);
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: targetBlock, oldBlock: oldTargetBlock }],
                    preventEventTrigger: true
                } });
        }
        // Pop the deletion action from the undo stack if user selected a content and pasted
        if (isSelectivePaste) {
            this.undoRedoAction.applyNextUndoSibling();
        }
        this.parent.stateManager.updateManagerBlocks();
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    handleClipboardRedo(currentState) {
        const { type, blocks, targetBlockId, clipboardData, newContent, isPastedAtStart, isSelectivePaste } = currentState.data;
        // Pop the deletion action from the redo stack if user selected a content and pasted
        if (isSelectivePaste) {
            this.undoRedoAction.applyNextRedoSibling();
        }
        const editorBlocks = this.parent.getEditorBlocks();
        const targetBlock = getBlockModelById(targetBlockId, editorBlocks);
        const oldTargetBlock = decoupleReference(targetBlock);
        const clipboardBlocks = clipboardData ? clipboardData.blocks : null;
        const nonMergableTypes = [BlockType.Table, BlockType.Image, BlockType.Divider];
        const isFirstBlkNonMergableType = clipboardBlocks ?
            nonMergableTypes.indexOf(clipboardData.blocks[0].blockType) !== -1 : false;
        let isFirstBlkProcessed = false;
        if (type === 'blocks') {
            const isEmptyTargetBlock = targetBlock && targetBlock.content
                && ((targetBlock.content.length === 1 && targetBlock.content[0].contentType === ContentType.Text &&
                    ((targetBlock.content[0].content === '' || !targetBlock.content[0].content)))
                    || !currentState.oldBlockModel.content.length);
            if (isEmptyTargetBlock) {
                isFirstBlkProcessed = true;
                const block = decoupleReference(clipboardBlocks[0]);
                this.parent.blockService.generateNewIdsForBlock(block);
                block.id = targetBlockId;
                this.parent.blockService.replaceBlock(targetBlock.id, block);
                this.parent.stateManager.updateManagerBlocks();
                const updatedBlockModel = getBlockModelById(block.id, this.parent.getEditorBlocks());
                this.parent.observer.notify('modelChanged', { type: 'ReplaceBlock', state: {
                        targetBlockId: targetBlockId,
                        block: updatedBlockModel,
                        oldBlock: oldTargetBlock,
                        preventEventTrigger: true
                    } });
            }
            else if (!isFirstBlkNonMergableType) {
                isFirstBlkProcessed = true;
                if (!isSelectivePaste) {
                    this.undoRedoAction.applyNextRedoSibling();
                }
                const originalBlock = getBlockModelById(targetBlockId, this.parent.getEditorBlocks());
                const originalClone = decoupleReference(originalBlock);
                this.parent.blockService.updateContent(originalBlock.id, [
                    ...originalBlock.content,
                    ...clipboardBlocks[0].content
                ]);
                this.parent.stateManager.updateManagerBlocks();
                this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                        data: [{ block: originalBlock, oldBlock: originalClone }],
                        preventEventTrigger: true
                    } });
            }
            this.parent.blockCommand.addBulkBlocks({
                blocks: (clipboardBlocks.slice(!isFirstBlkProcessed ? 0 : 1)),
                targetBlockId: targetBlockId,
                isUndoRedoAction: true,
                insertionType: 'blocks'
            });
        }
        else if (type === 'block') {
            this.parent.blockCommand.addBulkBlocks({
                blocks: blocks,
                targetBlockId: targetBlockId,
                isUndoRedoAction: true,
                insertionType: 'block'
            });
        }
        else if (type === 'content') {
            this.parent.blockService.updateContent(targetBlock.id, newContent);
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: targetBlock, oldBlock: oldTargetBlock }],
                    preventEventTrigger: true
                } });
            const pasteEndOffset = currentState.data.pasteEndOffset;
            if (!isNullOrUndefined(pasteEndOffset)) {
                const blockElement = this.parent.getBlockElementById(targetBlock.id);
                if (blockElement) {
                    const contentEl = getBlockContentElement(blockElement);
                    this.parent.setFocusToBlock(blockElement);
                    setCursorPosition(contentEl, pasteEndOffset);
                }
            }
        }
        this.parent.stateManager.updateManagerBlocks();
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    /**
     * Handles undo redo for block addition and deletion
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    blockAdditionDeletionUndoRedo(currentState) {
        switch (currentState.action) {
            case actionType.blockAdded: {
                if (this.undoRedoAction.isUndoing) {
                    this.removeBlock(currentState);
                }
                else {
                    this.createBlock(currentState);
                }
                break;
            }
            case actionType.blockRemoved:
                if (this.undoRedoAction.isUndoing) {
                    this.createBlock(currentState);
                    // If last deletion was a soft-selected special block, restore overlay on undo
                    const restoredId = currentState.oldBlockModel && currentState.oldBlockModel.id;
                    if (restoredId && this.parent.lastHighlightedBlockId === restoredId) {
                        const blockEle = this.parent.getBlockElementById(restoredId);
                        if (blockEle) {
                            this.parent.selectionOverlay.show(currentState.oldBlockModel.id);
                            if (this.parent.nodeSelection) {
                                this.parent.nodeSelection.clearSelection();
                            }
                        }
                    }
                }
                else {
                    this.removeBlock(currentState);
                }
                break;
        }
    }
    /**
     * Creates block with given state
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    createBlock(currentState) {
        const deletedBLockIndex = currentState.data ? currentState.data.currentIndex : -1;
        if (deletedBLockIndex < 0) {
            return;
        }
        const { blockBeforeSplit, blocksAfterSplit, isSplitting, isTargetDeletion } = (currentState.data);
        const parentId = blockBeforeSplit ? blockBeforeSplit.parentId : currentState.oldBlockModel.parentId;
        const parentBlock = getBlockModelById(parentId, this.parent.getEditorBlocks());
        const parentCell = findCellById(parentId, this.parent.getEditorBlocks());
        const targetIndex = deletedBLockIndex === 0 ? deletedBLockIndex : deletedBLockIndex - 1;
        const afterBlockModel = parentBlock
            ? parentBlock.properties.children[targetIndex]
            : (parentCell ? parentCell.blocks[targetIndex] : this.parent.getEditorBlocks()[targetIndex]);
        let currentBlockElement;
        const addedBlock = this.parent.blockCommand.addBlock({
            targetBlock: currentBlockElement || this.parent.blockContainer.querySelector('#' + afterBlockModel.id),
            blockType: isSplitting ? blocksAfterSplit[1].blockType : currentState.oldBlockModel.blockType,
            block: isSplitting ? blocksAfterSplit[1] : currentState.oldBlockModel,
            isAfter: deletedBLockIndex > 0,
            isUndoRedoAction: true,
            preventEventTrigger: true,
            forceIgnoreTargetUpdate: true
        });
        if (isSplitting && blocksAfterSplit) {
            const cursorBlock = blocksAfterSplit[0];
            this.parent.blockService.replaceBlock(cursorBlock.id, cursorBlock);
            this.parent.stateManager.updateManagerBlocks();
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: getBlockModelById(cursorBlock.id, this.parent.getEditorBlocks()) }],
                    preventEventTrigger: true
                } });
        }
        else {
            const newBlockElement = this.parent.getBlockElementById(addedBlock.id);
            const direction = isTargetDeletion ? 'next' : 'previous';
            const adjacentBlock = getAdjacentBlock(newBlockElement, direction);
            const canSetFocusToAdjacent = (adjacentBlock &&
                (isTargetDeletion || isNonContentEditableBlock(currentState.oldBlockModel.blockType)));
            if (canSetFocusToAdjacent) {
                this.parent.setFocusAndUIForNewBlock(adjacentBlock);
            }
            else if (currentState.oldBlockModel.blockType === BlockType.Callout) {
                this.parent.setFocusAndUIForNewBlock(newBlockElement.querySelector('.' + constants.BLOCK_CLS));
            }
            else if (currentState.oldBlockModel.blockType === BlockType.Quote) {
                this.parent.setFocusAndUIForNewBlock(newBlockElement.querySelector('.' + constants.BLOCK_CLS));
            }
        }
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    /**
     * Removes block with given state
     *
     * @param {IUndoRedoState} currentState - Specifies the current state of the undo redo action
     * @returns {void} - Returns void
     * @hidden
     */
    removeBlock(currentState) {
        const { blockBeforeSplit, isAfter, isSplitting } = (currentState.data);
        const blockElement = this.parent.blockContainer.querySelector(`#${currentState.data.blockId}`);
        if (isSplitting) {
            const targetBlockElement = this.parent.getBlockElementById(blockBeforeSplit.id);
            const newCursorPos = getBlockContentElement(targetBlockElement).textContent.length;
            const newBlock = blockBeforeSplit;
            this.parent.blockCommand.deleteBlock({ blockElement: blockElement, isUndoRedoAction: true, preventEventTrigger: true });
            this.parent.blockService.replaceBlock(blockBeforeSplit.id, newBlock);
            this.parent.stateManager.updateManagerBlocks();
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: getBlockModelById(blockBeforeSplit.id, this.parent.getEditorBlocks()) }],
                    preventEventTrigger: true
                } });
            this.parent.setFocusToBlock(targetBlockElement);
            setCursorPosition(getBlockContentElement(targetBlockElement), newCursorPos);
        }
        else {
            const direction = isAfter ? 'previous' : 'next';
            let adjacentBlock = getAdjacentBlock(blockElement, direction);
            if (!adjacentBlock) {
                // Fallback - try with opposite direction
                adjacentBlock = getAdjacentBlock(blockElement, (direction === 'previous' ? 'next' : 'previous'));
            }
            this.parent.setFocusAndUIForNewBlock(adjacentBlock);
            this.parent.blockCommand.deleteBlock({ blockElement: blockElement, isUndoRedoAction: true, preventEventTrigger: true });
        }
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
}
